import { useOptionalSession } from "@/auth/SessionContext";
import { useRuntimeConfig } from "@/config/RuntimeConfigContext";
import { shortHash } from "@/lib/format";
import { ENVIRONMENT_LABEL } from "@/lib/labels";
import { Badge } from "@/ui/Badge";

/**
 * Environment identity, always on screen.
 *
 * The product reference hides which environment the user is operating in. That
 * is unacceptable here: every action in this editor is scoped to an
 * environment, and a clinician must never have to infer it from a slug in the
 * address bar.
 *
 * It comes from the SESSION (#18, ruling 2026-08-02). It used to come from a
 * deploy-time `/config.json` value that nothing reconciled against the server's
 * answer, so a typo there mislabelled every screen.
 *
 * This badge USED to carry a tenant name beside the environment, from the same
 * session, for the same reason. #202 removed it: Mentaily Entra authenticates
 * an internal Editor user and selects no clinical tenant, so there is no
 * organisation to name — and #203 lists the "<tenant> · <environment>" pair
 * among the tenant-scoped shell affordances to remove. The test asserts no
 * organisation name renders at all.
 *
 * Deployed environments additionally name their deployment pin from the
 * checked-in runtime configuration. Atomic releases pin the complete
 * deployment snapshot with `deploymentCommit`; the older delivery shape has
 * only `commit`. The pin is shown only when the session and configuration
 * agree on the same non-local environment, so values from two environments can
 * never be composed into one header identity.
 */
export function EnvironmentBadge() {
  const config = useRuntimeConfig();
  const session = useOptionalSession()?.session;
  const environment = session?.environment ?? config.environment;
  const label = ENVIRONMENT_LABEL[environment] ?? environment;
  const deploymentPin =
    environment !== "local" && config.environment === environment
      ? (config.release?.deploymentCommit ?? config.release?.commit)
      : undefined;

  return (
    <div className="flex items-center gap-2">
      <Badge tone={environment === "staging" ? "warning" : "outline"}>
        <span className="sr-only">Environment: </span>
        {label}
      </Badge>
      {deploymentPin ? (
        <Badge
          tone="outline"
          className="font-mono"
          title={`Deployment pin: ${deploymentPin}`}
        >
          <span className="sr-only">Deployment pin: {deploymentPin}. </span>
          <span aria-hidden="true">Pin {shortHash(deploymentPin)}</span>
        </Badge>
      ) : null}
    </div>
  );
}
